import first from "lodash/first";
import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { homeAdvertiserV2ContentItemContent$key } from "scmp-app/queries/__generated__/homeAdvertiserV2ContentItemContent.graphql";

import {
  Container,
  Content,
  CoverImage,
  Headline,
  SponsorContainer,
  SponsorName,
  SponsorType,
  StyledEntityLink,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: homeAdvertiserV2ContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment homeAdvertiserV2ContentItemContent on Content {
        ...entityLink
        entityId
        urlAlias
        topics {
          sponsor {
            name
            type
          }
        }
        ...hooksContentItemProvidersContent
          @arguments(withCoverImage: true, withCoverImageSize540x360: true, withHeadline: true)
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(content);

  const sponsor = first(content?.topics)?.sponsor;
  const sponsorName = sponsor?.name;
  const sponsorType = sponsor?.type;

  return (
    <Container className={className} ref={ref}>
      <StyledEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size540x360",
              tabletUp: "size540x360",
            },
            withCaption: false,
          })}
        </CoverImage>
        <Content>
          <Headline>
            {providers.headline({
              preferSocialHeadline: true,
              skipHeadlineSponsorStyle: true,
            })}
          </Headline>
        </Content>
      </StyledEntityLink>
      <SponsorContainer>
        <SponsorType>{sponsorType}</SponsorType>
        <SponsorName>{sponsorName}</SponsorName>
      </SponsorContainer>
    </Container>
  );
};

Component.displayName = "ContentItemHomeAdvertiserV2";

export const ContentItemHomeAdvertiserV2 = withHighlightedHeadline(Component);
